//----------------------------------------------------------------------------------------------------------------------
// Sound Behavior
//----------------------------------------------------------------------------------------------------------------------

import { SoundState, type StaticSound, type TransformNode } from '@babylonjs/core';

import { GameEntityBehavior } from '../classes/entity.ts';
import type { GameEngine } from '../classes/gameEngine.ts';
import type { GameEvent } from '../classes/eventBus.ts';
import type { LoggerInterface } from '../interfaces/logger.ts';
import type { AudioManager } from '../managers/audio.ts';
import { SAGELogger } from '../utils/logger.ts';

//----------------------------------------------------------------------------------------------------------------------

/**
 * Configuration for a single sound.
 */
export interface SoundConfig
{
    /** Path to the sound file */
    url : string;

    /** Volume level (0-1), default 1 */
    volume ?: number;

    /** Whether to loop the sound, default false */
    loop ?: boolean;

    /** Whether to use spatial 3D audio, default false */
    spatial ?: boolean;

    /** Maximum distance for spatial audio, default 100 */
    maxDistance ?: number;

    /** Whether to autoplay when registered, default false */
    autoplay ?: boolean;

    /** Audio channel name (e.g., 'sfx', 'music'). Used with AudioManager. */
    channel ?: string;
}

/**
 * State interface for entities using SoundBehavior.
 * Entity state should include a `sounds` property with sound configurations.
 */
export interface SoundEntityState
{
    /** Map of sound name to configuration */
    sounds ?: Record<string, SoundConfig>;
}

//----------------------------------------------------------------------------------------------------------------------

/**
 * Behavior for managing sounds on an entity.
 *
 * Sounds are configured in the entity's state under the `sounds` property.
 * The behavior creates sounds via AudioManager (BabylonJS AudioV2).
 *
 * Requires AudioManager to be configured on the GameEngine. If no AudioManager is found,
 * a warning is logged and no sounds are created.
 *
 * @example
 * ```typescript
 * // Entity definition
 * {
 *     type: 'player',
 *     behaviors: [SoundBehavior, ...],
 *     defaultState: {
 *         sounds: {
 *             jump: { url: 'sounds/jump.mp3', volume: 0.8, channel: 'sfx' },
 *             footstep: { url: 'sounds/footstep.mp3', loop: true, channel: 'sfx' }
 *         }
 *     }
 * }
 *
 * // In another behavior, get the sound behavior and control sounds
 * const soundBehavior = entity.getBehavior(SoundBehavior);
 * soundBehavior?.play('jump');
 * ```
 */
export class SoundBehavior extends GameEntityBehavior<SoundEntityState>
{
    name = 'sound';
    eventSubscriptions : string[] = [];

    /** Map of sound name to StaticSound instance */
    private _sounds = new Map<string, StaticSound>();

    /** Whether sounds have been initialized */
    private _initialized = false;

    /** AudioManager reference */
    private _audioManager : AudioManager | null = null;

    /** Node reference for spatial detach on cleanup */
    private _node : TransformNode | null = null;

    private _log : LoggerInterface = new SAGELogger('SoundBehavior');

    //------------------------------------------------------------------------------------------------------------------
    // Lifecycle
    //------------------------------------------------------------------------------------------------------------------

    onNodeAttached(node : TransformNode, gameEngine : GameEngine) : void
    {
        if(this._initialized)
        {
            return;
        }

        this._node = node;
        this._initialized = true;

        this._log = gameEngine.logger.getLogger('SoundBehavior');

        if(gameEngine.managers.audioManager)
        {
            this._audioManager = gameEngine.managers.audioManager;
        }
        else
        {
            this._log.warn('No AudioManager configured. Sounds will not be created.');
            return;
        }

        if(!this.entity)
        {
            return;
        }

        const state = this.entity.state;
        const soundConfigs = state.sounds;
        if(!soundConfigs)
        {
            return;
        }

        for(const [ name, config ] of Object.entries(soundConfigs))
        {
            this._createSound(name, config as SoundConfig);
        }
    }

    processEvent(_event : GameEvent, _state : SoundEntityState) : boolean
    {
        return false;
    }

    onReset(_state : SoundEntityState) : void
    {
        this._disposeSounds();
    }

    async destroy() : Promise<void>
    {
        this._disposeSounds();
    }

    //------------------------------------------------------------------------------------------------------------------
    // Private Methods
    //------------------------------------------------------------------------------------------------------------------

    private _createSound(name : string, config : SoundConfig) : void
    {
        if(!this._audioManager || !this.entity)
        {
            return;
        }

        const entityId = this.entity.id;
        const node = this._node;

        this._audioManager.createSound(
            `${ entityId }_${ name }`,
            config.url,
            config.channel,
            {
                loop: config.loop ?? false,
                autoplay: config.autoplay ?? false,
                volume: config.volume ?? 1,
                spatialEnabled: config.spatial ?? false,
                spatialMaxDistance: config.maxDistance ?? 100,
                spatialDistanceModel: 'linear',
            }
        )
            .then((sound) =>
            {
                this._sounds.set(name, sound);

                if((config.spatial ?? false) && node)
                {
                    sound.spatial.attach(node);
                }
            })
            .catch((err) =>
            {
                this._log.error(`Failed to create sound "${ name }": ${ err }`);
            });
    }

    private _disposeSounds() : void
    {
        for(const sound of this._sounds.values())
        {
            sound.dispose();
        }
        this._sounds.clear();
        this._initialized = false;
        this._node = null;
        this._audioManager = null;
    }

    //------------------------------------------------------------------------------------------------------------------
    // Public API
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Play a sound by name.
     *
     * @param name - The name of the sound to play. If omitted and only one sound exists, plays that sound.
     * @param time - Optional time offset in seconds to start playing from
     */
    play(name ?: string, time ?: number) : void
    {
        const sound = this._getSound(name);
        if(sound)
        {
            if(time !== undefined)
            {
                sound.play({ startOffset: time });
            }
            else
            {
                sound.play();
            }
        }
    }

    /**
     * Stop a sound by name.
     *
     * @param name - The name of the sound to stop. If omitted, stops all sounds.
     */
    stop(name ?: string) : void
    {
        if(name)
        {
            const sound = this._sounds.get(name);
            if(sound)
            {
                sound.stop();
            }
        }
        else
        {
            for(const sound of this._sounds.values())
            {
                sound.stop();
            }
        }
    }

    /**
     * Pause a sound by name.
     *
     * @param name - The name of the sound to pause. If omitted and only one sound exists, pauses that sound.
     */
    pause(name ?: string) : void
    {
        const sound = this._getSound(name);
        if(sound)
        {
            sound.pause();
        }
    }

    /**
     * Set the volume of a sound.
     *
     * @param volume - Volume level (0-1)
     * @param name - The name of the sound. If omitted, sets volume for all sounds.
     */
    setVolume(volume : number, name ?: string) : void
    {
        if(name)
        {
            const sound = this._sounds.get(name);
            if(sound)
            {
                sound.volume = volume;
            }
        }
        else
        {
            for(const sound of this._sounds.values())
            {
                sound.volume = volume;
            }
        }
    }

    /**
     * Check if a sound is currently playing.
     *
     * @param name - The name of the sound. If omitted and only one sound exists, checks that sound.
     */
    isPlaying(name ?: string) : boolean
    {
        const sound = this._getSound(name);
        return sound?.state === SoundState.Started;
    }

    /**
     * Get all registered sound names.
     */
    getSoundNames() : string[]
    {
        return Array.from(this._sounds.keys());
    }

    /**
     * Check if a sound with the given name exists.
     *
     * @param name
     */
    hasSound(name : string) : boolean
    {
        return this._sounds.has(name);
    }

    /**
     * Register a new sound at runtime. Replaces any existing sound with the same name.
     *
     * @param name
     * @param config
     */
    registerSound(name : string, config : SoundConfig) : void
    {
        if(!this._initialized || !this._audioManager)
        {
            throw new Error('SoundBehavior must be initialized before registering sounds');
        }

        const existing = this._sounds.get(name);
        if(existing)
        {
            existing.dispose();
        }

        this._createSound(name, config);
    }

    /**
     * Unregister and dispose a sound.
     *
     * @param name
     */
    unregisterSound(name : string) : void
    {
        const sound = this._sounds.get(name);
        if(sound)
        {
            sound.dispose();
            this._sounds.delete(name);
        }
    }

    //------------------------------------------------------------------------------------------------------------------
    // Helper Methods
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Get a sound by name. If name is omitted and only one sound exists, returns that sound.
     */
    private _getSound(name ?: string) : StaticSound | undefined
    {
        if(name)
        {
            return this._sounds.get(name);
        }

        if(this._sounds.size === 1)
        {
            return this._sounds.values().next().value;
        }

        return undefined;
    }
}

//----------------------------------------------------------------------------------------------------------------------
